import { LOGO_URL, APP_NAME, COMPANY_NAME, CONTACT_EMAIL, CONTACT_PHONE } from '../lib/constants';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const CookiePolicy = () => {
  const lastUpdated = "January 3, 2025";
  const dpoEmail = "DPO@myprivicy.com";
  
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt={APP_NAME} className="h-8" />
            <span className="font-bold text-[#0F172A] font-['Manrope']">{APP_NAME}</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-[#64748B]">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-2 font-['Manrope']">
            Cookie Policy
          </h1>
          <p className="text-[#64748B] mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">1. Introduction</h2>
              <p className="text-[#475569] mb-4">
                This Cookie Policy explains how {COMPANY_NAME} ("{APP_NAME}", "we", "us", or "our") uses 
                cookies and similar tracking technologies when you visit our website and use our services. 
                This policy should be read alongside our{' '}
                <Link to="/privacy" className="text-[#3B82F6] hover:underline">Privacy Policy</Link>.
              </p>
              <p className="text-[#475569] mb-4">
                This policy is designed to comply with cookie and data protection requirements under the 
                laws of Jamaica, Barbados, Bermuda, and the Cayman Islands.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">2. What Are Cookies?</h2>
              <p className="text-[#475569] mb-4">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) 
                when you visit a website. They are widely used to make websites work more efficiently and 
                to provide information to website owners.
              </p>
              <p className="text-[#475569] mb-4">
                Cookies can be "persistent" or "session" cookies:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li><strong>Persistent cookies</strong> remain on your device for a set period or until you delete them</li>
                <li><strong>Session cookies</strong> are deleted when you close your browser</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">3. How We Use Cookies</h2>
              <p className="text-[#475569] mb-4">
                We use cookies and similar technologies for the following purposes:
              </p>

              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-[#0F172A] mb-2">3.1 Essential Cookies (Strictly Necessary)</h3>
                <p className="text-[#475569] mb-2">
                  These cookies are essential for the website to function properly. They cannot be switched off.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-[#0F172A]">Cookie</th>
                      <th className="text-left py-2 text-[#0F172A]">Purpose</th>
                      <th className="text-left py-2 text-[#0F172A]">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#475569]">
                    <tr className="border-b border-slate-100">
                      <td className="py-2">auth_token</td>
                      <td className="py-2">Maintains your login session</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2">admin_token</td>
                      <td className="py-2">Admin authentication</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                    <tr>
                      <td className="py-2">cookie_consent</td>
                      <td className="py-2">Stores your cookie preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-[#0F172A] mb-2">3.2 Functional Cookies</h3>
                <p className="text-[#475569] mb-2">
                  These cookies enable enhanced functionality and personalization.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-[#0F172A]">Cookie</th>
                      <th className="text-left py-2 text-[#0F172A]">Purpose</th>
                      <th className="text-left py-2 text-[#0F172A]">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#475569]">
                    <tr className="border-b border-slate-100">
                      <td className="py-2">assessment_progress</td>
                      <td className="py-2">Saves your assessment progress</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr>
                      <td className="py-2">user_preferences</td>
                      <td className="py-2">Remembers display preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-[#0F172A] mb-2">3.3 Analytics Cookies</h3>
                <p className="text-[#475569] mb-2">
                  These cookies help us understand how visitors interact with our website.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-[#0F172A]">Cookie</th>
                      <th className="text-left py-2 text-[#0F172A]">Purpose</th>
                      <th className="text-left py-2 text-[#0F172A]">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#475569]">
                    <tr>
                      <td className="py-2">_analytics</td>
                      <td className="py-2">Tracks page views and usage patterns</td>
                      <td className="py-2">2 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">4. Third-Party Cookies</h2>
              <p className="text-[#475569] mb-4">
                We may use third-party services that set their own cookies:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>
                  <strong>Calendly:</strong> When you use our consultation booking feature, Calendly may 
                  set cookies to facilitate scheduling. See{' '}
                  <a href="https://calendly.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline">
                    Calendly's Privacy Policy
                  </a>.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">5. Your Cookie Choices</h2>
              <p className="text-[#475569] mb-4">
                You have several options to control cookies:
              </p>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-3">5.1 Browser Settings</h3>
              <p className="text-[#475569] mb-4">
                Most web browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>Block all cookies</li>
                <li>Accept only first-party cookies</li>
                <li>Delete cookies when you close your browser</li>
                <li>Browse in "private" or "incognito" mode</li>
              </ul>
              <p className="text-[#475569] mb-4">
                Please note that blocking essential cookies may affect the functionality of our Service.
              </p>

              <h3 className="text-lg font-semibold text-[#0F172A] mb-3">5.2 Browser-Specific Instructions</h3>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline">
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline">
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline">
                    Safari
                  </a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline">
                    Microsoft Edge
                  </a>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">6. Similar Technologies</h2>
              <p className="text-[#475569] mb-4">
                In addition to cookies, we may use similar technologies:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>
                  <strong>Local Storage:</strong> Used to store data locally on your device for better 
                  performance. This includes authentication tokens and user preferences.
                </li>
                <li>
                  <strong>Session Storage:</strong> Temporary storage that is cleared when you close 
                  your browser tab. Used for temporary data during your session.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">7. Legal Basis</h2>
              <p className="text-[#475569] mb-4">
                Under Caribbean data protection laws, we rely on the following legal bases for using cookies:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>
                  <strong>Essential cookies:</strong> Legitimate interest - these are necessary for the 
                  website to function and do not require consent.
                </li>
                <li>
                  <strong>Functional and analytics cookies:</strong> Consent - by continuing to use our 
                  website after being informed about cookies, you consent to their use.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">8. Changes to This Policy</h2>
              <p className="text-[#475569] mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices 
                or for other operational, legal, or regulatory reasons. We encourage you to review this 
                policy periodically. The "Last Updated" date at the top indicates when this policy was 
                last revised.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">9. Contact Us</h2>
              <p className="text-[#475569] mb-4">
                If you have questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-[#0F172A] font-semibold mb-2">{COMPANY_NAME}</p>
                <p className="text-[#475569] flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#3B82F6] hover:underline">{CONTACT_EMAIL}</a>
                </p>
                <p className="text-[#475569] flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${dpoEmail}`} className="text-[#3B82F6] hover:underline">{dpoEmail}</a> (Data Protection Officer)
                </p>
                <p className="text-[#475569] flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {CONTACT_PHONE}
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-[#64748B]">
          <Link to="/terms" className="hover:text-[#3B82F6]">Terms and Conditions</Link>
          <Link to="/privacy" className="hover:text-[#3B82F6]">Privacy Policy</Link>
          <Link to="/" className="hover:text-[#3B82F6]">Back to Home</Link>
        </div>
      </main>
    </div>
  );
};

export default CookiePolicy;
